"use client";

import { useTranslations } from 'next-intl';

import { useEffect, useRef, useState } from "react";

import ReviewCard from './ReviewCard';


const Reviews = () => {
  const t = useTranslations('home.reviews');
  const [showScrollbar, setShowScrollbar] = useState(false);

  // Get slides from translations
  const slidesObj = t.raw('slides');
  const slidesData = slidesObj ? Object.values(slidesObj) : [];

  // Static image data
  const imageData = [
    { image: "/images/reviews/review1.webp", avatar: "/images/reviews/review1-ava.png" },
    { image: "/images/reviews/review2.webp", avatar: "/images/reviews/review2-ava.png" },
    { image: "/images/reviews/review3.webp", avatar: "/images/reviews/review3-ava.png" },
    { image: "/images/reviews/review4.webp", avatar: "/images/reviews/review4-ava.png" },
  ];

  // Merge translation data with image paths
  const slides = slidesData.map((slide, index) => ({
    id: index + 1,
    name: slide.name,
    review: slide.review,
    rating: 5,
    ...imageData[index]
  }));

  const scrollRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    const slider = scrollRef.current;
    let isDown = false;
    let startX;
    let scrollLeft;
    let hasMoved = false;

    const handleScroll = () => {
      // Show scrollbar when scrolling starts
      setShowScrollbar(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Hide scrollbar after 2 seconds of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollbar(false);
      }, 2000);
    };

    const mouseDownHandler = (e) => {
      // Only start drag if clicking on the container, not on images
      if (e.target.tagName === 'IMG') return;

      isDown = true;
      hasMoved = false;
      slider.classList.add("cursor-grabbing");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const mouseLeaveOrUpHandler = () => {
      isDown = false;
      hasMoved = false;
      slider.classList.remove("cursor-grabbing");
    };

    const mouseMoveHandler = (e) => {
      if (!isDown) return;
      e.preventDefault();
      hasMoved = true;
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1.5; // Scroll speed multiplier
      slider.scrollLeft = scrollLeft - walk;
    };

    // Prevent image dragging
    const preventDragHandler = (e) => {
      e.preventDefault();
    };

    slider.addEventListener("scroll", handleScroll);
    slider.addEventListener("mousedown", mouseDownHandler);
    slider.addEventListener("mouseleave", mouseLeaveOrUpHandler);
    slider.addEventListener("mouseup", mouseLeaveOrUpHandler);
    slider.addEventListener("mousemove", mouseMoveHandler);

    // Prevent default drag behavior on images
    const images = slider.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('dragstart', preventDragHandler);
    });

    return () => {
      slider.removeEventListener("scroll", handleScroll);
      slider.removeEventListener("mousedown", mouseDownHandler);
      slider.removeEventListener("mouseleave", mouseLeaveOrUpHandler);
      slider.removeEventListener("mouseup", mouseLeaveOrUpHandler);
      slider.removeEventListener("mousemove", mouseMoveHandler);

      images.forEach(img => {
        img.removeEventListener('dragstart', preventDragHandler);
      });

      // Clear timeout on cleanup
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full max-w-[1034px] mx-auto px-4 section-py">
      <h2 className="text-title text-center !text-[#2B2B2B] !mb-2">{t('title')}</h2>
      <h2 className="text-title text-center !mb-0 ">{t('subTitle')}</h2>
      <div className="flex gap-1 justify-center items-center mt-2 md:mt-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <svg
            className="filter drop-shadow-[0_2px_2px_rgba(0,0,0,0.25)]" key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.9398 9.06144C23.8678 8.81773 23.7252 8.60082 23.53 8.43808C23.3348 8.27535 23.0957 8.17407 22.8431 8.14704L15.9143 7.50144L13.1758 0.807842C13.085 0.570201 12.9242 0.365732 12.7146 0.221482C12.5051 0.0772329 12.2567 0 12.0023 0C11.7478 0 11.4994 0.0772329 11.2899 0.221482C11.0803 0.365732 10.9195 0.570201 10.8287 0.807842L8.09025 7.50144L1.16025 8.15304C0.908449 8.18008 0.670176 8.28064 0.475136 8.44217C0.280096 8.60371 0.136917 8.81908 0.0634503 9.06144C-0.0143746 9.30834 -0.0207407 9.57222 0.0450878 9.82258C0.110916 10.0729 0.246252 10.2996 0.43545 10.4762L5.67225 15.2678L4.12905 22.3706C4.07251 22.6231 4.0891 22.8864 4.17688 23.1298C4.26466 23.3731 4.41998 23.5864 4.62465 23.7446C4.82381 23.897 5.0648 23.9849 5.3153 23.9965C5.56581 24.0081 5.81388 23.9428 6.02625 23.8094L12.0023 20.0834L17.9759 23.8106C18.1884 23.9441 18.4367 24.0094 18.6874 23.9979C18.9381 23.9863 19.1793 23.8983 19.3787 23.7458C19.5834 23.5877 19.7388 23.3744 19.8266 23.131C19.9144 22.8877 19.9309 22.6243 19.8743 22.3718L18.3299 15.275L23.5667 10.4834C23.7573 10.3062 23.8935 10.0784 23.9594 9.82654C24.0253 9.57471 24.0193 9.30932 23.9398 9.06144Z" fill="url(#paint0_linear_1956_3365)" />
            <defs>
              <linearGradient id="paint0_linear_1956_3365" x1="12.002" y1="0" x2="12.002" y2="23.9992" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFBA33" />
                <stop offset="1" stopColor="#F6D338" />
              </linearGradient>
            </defs>
          </svg>
        ))}
      </div>
      <div
        className={`mx-auto pb-2 md:pb-0 w-full mt-10 overflow-x-scroll select-none cursor-grab ${showScrollbar ? 'scrollbar-ultra' : 'scrollbar-ultra-hidden'
          }`}
        ref={scrollRef}
        style={{
          scrollbarGutter: 'stable'
        }}
      >
        <div className="flex gap-6 items-center p-2">
          {slides.map((slide) => (
            <ReviewCard key={slide.id} slide={slide} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reviews;